import { Avatar } from "@hakistudio/hakiui";
import { formatDistanceToNow } from "date-fns";
import { Bell } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import {
  useAcceptInviteNotification,
  useDeclineInviteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type Notification,
} from "@/lib/notifications";

/**
 * Inbox for invites, shares and comments. A popover, not a modal — the rest of
 * the app stays reachable.
 *
 * Anything that needs consent — a team invite, a note someone wants to share —
 * keeps Accept / Decline until it is answered, and is not a link in the
 * meantime: a shared note is not yours to open until you have taken it. The
 * server decides what is still pending, so a stale panel cannot answer twice.
 */
export function NotificationBell() {
  const { data } = useNotifications();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const unread = data?.unread ?? 0;

  return (
    <>
      <span ref={buttonRef} className="relative inline-flex">
        <Button
          variant="outline"
          size="icon"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
          aria-expanded={open}
          aria-controls={panelId}
          title="Notifications"
          onClick={() => setOpen((v) => !v)}
        >
          <Bell className="h-4 w-4" />
        </Button>
        {unread > 0 ? (
          <span className="pointer-events-none absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-pill bg-accent px-1 text-[10px] font-semibold text-tile-ink">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </span>
      {open
        ? createPortal(
            <NotificationPanel
              id={panelId}
              anchor={buttonRef.current}
              items={data?.notifications ?? []}
              unread={unread}
              onClose={() => setOpen(false)}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function NotificationPanel({
  id,
  anchor,
  items,
  unread,
  onClose,
}: {
  id: string;
  anchor: HTMLButtonElement | null;
  items: Notification[];
  unread: number;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const markAll = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const accept = useAcceptInviteNotification();
  const decline = useDeclineInviteNotification();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchor?.contains(t)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [anchor, onClose]);

  const box = anchor?.getBoundingClientRect();
  const mobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const style: React.CSSProperties = mobile
    ? { top: "calc(env(safe-area-inset-top, 0px) + 3.75rem)", left: "0.75rem", right: "0.75rem" }
    : {
        top: Math.min((box?.bottom ?? 48) + 8, window.innerHeight - 24),
        left: Math.min(Math.max(8, (box?.left ?? 16) - 220), window.innerWidth - 368),
      };

  const go = (n: Notification) => {
    if (n.pending) return;
    if (!n.readAt) markRead.mutate(n.id);
    if (n.href) {
      onClose();
      navigate(n.href);
    }
  };

  const actionableUnread = items.some((n) => !n.pending && !n.readAt);

  return (
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-label="Notifications"
      style={style}
      className={cn(
        "fixed z-40 flex max-h-[min(28rem,70vh)] w-[min(calc(100vw-1.5rem),22rem)] flex-col",
        "overflow-hidden rounded-card border border-line bg-raised shadow-card",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95",
      )}
    >
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 className="text-sm font-semibold text-ink">Notifications</h2>
        {actionableUnread ? (
          <button
            type="button"
            className="text-xs font-medium text-ink-muted underline-offset-4 hover:text-ink hover:underline"
            onClick={() => markAll.mutate()}
          >
            Mark rest read
          </button>
        ) : null}
      </header>

      {items.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-ink-muted">You're all caught up.</p>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {items.map((n) => (
            <li
              key={n.id}
              className={cn(
                "flex gap-3 border-b border-line px-4 py-3 last:border-b-0",
                !n.readAt && "bg-sunken/50",
              )}
            >
              <Avatar
                src={n.actorAvatar ?? undefined}
                name={n.actorName ?? "?"}
                size="sm"
                radius="full"
                className="mt-0.5 shrink-0"
              />
              <div className="min-w-0 flex-1">
                {n.pending ? (
                  <p className="text-sm font-medium text-ink">{n.title}</p>
                ) : (
                  <button
                    type="button"
                    className="block w-full text-left text-sm font-medium text-ink hover:underline"
                    onClick={() => go(n)}
                  >
                    {n.title}
                  </button>
                )}
                {n.body ? <p className="mt-0.5 truncate text-xs text-ink-muted">{n.body}</p> : null}
                <p className="mt-1 text-xs text-ink-faint">
                  {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                </p>
                {n.pending ? (
                  <div className="mt-2.5 flex gap-2">
                    <Button
                      size="sm"
                      disabled={accept.isPending || decline.isPending}
                      onClick={() =>
                        accept.mutate(n.id, {
                          // The server says where accepting landed, so a shared
                          // note opens the note and an invite opens the team.
                          onSuccess: (res) => {
                            onClose();
                            navigate(res.href);
                          },
                        })
                      }
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={accept.isPending || decline.isPending}
                      onClick={() => decline.mutate(n.id)}
                    >
                      Decline
                    </Button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {unread === 0 && items.length > 0 ? (
        <p className="sr-only">No unread notifications</p>
      ) : null}
    </div>
  );
}
